// export { CalculatedFieldOperation } from './CalculatedFieldOperation';
// export * from './types';
import { useMemo, useState, useCallback } from "react";

import ThreeColumnLayout from "@/components/common/three-column-layout";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { ConfirmationModalBase } from "@/components/common/modals";
import FullModal from "../../full-modal";
import { InputOutput } from "../../InputOutput";
import { CalculatedFieldOperation } from "./CalculatedFieldOperation";
import {
  CalculatedFieldProvider,
  useCalculatedFieldContext,
} from "./CalculatedFieldOperation/context";
import { useGetVariables } from "@/workflow_api/hooks/variables/useGetVariables";
import Variables from "@/workflow_api/components/variables";
import { Icon } from "@elaniintc/goes-design-system";
import { NodeTypes, type NodeData } from "@/workflow_api/types/workflow";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { useNodeNameValidation } from "@/workflow_api/hooks/useNodeNameValidation";

export const CalculatedFieldDetailsForm = () => {
  const [modalState, setModalState] = useState<"idle" | "close-warning">(
    "idle",
  );
  const {
    nodeDetailsOpen,
    setNodeDetailsOpen,
    setSelectedNodeId,
    selectedNodeId,
  } = useUIStore();

  const {
    elements,
    selectedVariableType,
    selectedOperation,
    selectedVariable,
    isLoadingVariables,
    saveNode,
  } = useCalculatedFieldContext();

  const { getNodeById } = useWorkflowStore();
  const selectedNode = getNodeById(selectedNodeId || "");
  const nodeData = selectedNode?.data as unknown as NodeData;

  const [title, setTitle] = useState<string>(nodeData?.displayName || "");
  const [showNodeTitleValidation, setShowNodeTitleValidation] = useState(false);
  const { getNameValidationError, isNameValid } = useNodeNameValidation(selectedNodeId);
  const [manualConfirmation, setManualConfirmation] = useState(() => {
    return nodeData?.manual_continue ?? false;
  });

  // Función para validar el tipo de contenido según el tipo de variable
  const validateElementContent = useCallback(
    (
      element: {
        value?: string;
        hours?: string;
        days?: string;
        months?: string;
        years?: string;
      },
      type: string | undefined,
    ) => {
      if (!type) return false; // Tipo no definido es inválido

      switch (type) {
        case "number": {
          // Validar que sea un número válido (permite decimales, pero no solo punto)
          const value = element.value || "";
          return /^\d+(\.\d+)?$/.test(value) && !isNaN(Number(value));
        }
        case "string": {
          // Para strings, cualquier contenido no vacío es válido
          const stringValue = element.value || "";
          return stringValue.trim().length > 0;
        }
        case "date": {
          // Para operaciones de fecha que usan campos específicos (add/subtract)
          if (
            selectedOperation?.name === "date_add" ||
            selectedOperation?.name === "date_subtract"
          ) {
            // Validar que al menos uno de los campos de tiempo tenga un valor válido
            const hours = element.hours || "";
            const days = element.days || "";
            const months = element.months || "";
            const years = element.years || "";

            // Validar formato de horas (HH:MM o solo números para horas)
            const isValidHours =
              hours === "" ||
              /^([0-9]{1,3}):([0-5][0-9])$/.test(hours) || // Formato HH:MM
              /^[0-9]{1,3}$/.test(hours); // Solo números (se interpreta como horas)
            // Validar que días, meses y años sean números enteros
            const isValidDays = days === "" || /^\d+$/.test(days);
            const isValidMonths = months === "" || /^\d+$/.test(months);
            const isValidYears = years === "" || /^\d+$/.test(years);

            // Al menos uno debe tener valor y todos deben ser válidos
            const hasValue =
              hours !== "" || days !== "" || months !== "" || years !== "";
            return (
              hasValue &&
              isValidHours &&
              isValidDays &&
              isValidMonths &&
              isValidYears
            );
          }
          // Para otras operaciones de fecha, validar que tenga value
          const dateValue = element.value || "";
          return dateValue.length > 0;
        }
        case "boolean": {
          // Para booleanos, validar valores true/false
          const boolValue = element.value || "";
          return boolValue === "true" || boolValue === "false";
        }
        case "array": {
          // Para arrays, cualquier contenido no vacío es válido
          const arrayValue = element.value || "";
          return arrayValue.trim().length > 0;
        }
        default: {
          const defaultValue = element.value || "";
          return defaultValue.trim().length > 0;
        }
      }
    },
    [selectedOperation],
  );

  const isValid = useMemo(() => {
    if (!selectedVariableType) return false;
    if (!selectedOperation) return false;
    if (!selectedVariable) return false;

    // Si la operación necesita valores, verificar que haya elementos
    if (selectedOperation.needsValue && !elements.length) return false;

    // Validar elementos según su tipo
    if (selectedOperation.needsValue) {
      const hasInvalidElements = elements.some((el) => {
        // Si es una variable (type: "var"), debe tener un value (ID de variable seleccionada)
        if (el.type === "var") {
          return !el.value || el.value.trim() === "";
        }
        // Si es un input (type: "inp"), debe tener un value válido según el tipo
        return !validateElementContent(el, selectedVariableType);
      });

      if (hasInvalidElements) return false;
    }

    if (selectedOperation.hasMultipleValues) {
      const hasInvalidElements = elements.some((el) => {
        // Si es una variable (type: "var"), debe tener un value (ID de variable seleccionada)
        if (el.type === "var") {
          return !el.value || el.value.trim() === "";
        }
        // Si es un input (type: "inp"), debe tener un value válido según el tipo
        return !validateElementContent(el, selectedVariableType);
      });

      if (hasInvalidElements) return false;
    }

    return true;
  }, [
    selectedVariableType,
    selectedOperation,
    selectedVariable,
    elements,
    validateElementContent,
  ]);

  return (
    <>
      <ConfirmationModalBase
        isOpen={modalState === "close-warning"}
        acceptText="Salir sin guardar"
        cancelText="Cancelar"
        description="¿Estás seguro de que deseas salir? Todos los cambios que hayas realizado se perderán"
        title="Salir sin guardar"
        acceptVariant="danger-secondary"
        isLoading={false}
        onAccept={() => {
          setNodeDetailsOpen(false);
          setSelectedNodeId();
          setModalState("idle");
        }}
        onClose={() => {
          setModalState("idle");
        }}
      />
      <FullModal
        isOpen={nodeDetailsOpen}
        onClose={() => {
          setModalState("close-warning");
        }}
        isSaveValid={isValid && isNameValid(title)}
        onSave={async () => {
          setShowNodeTitleValidation(true);
          if (!isNameValid(title)) return;
          try {
            await saveNode({
              display_name: title,
              manual_continue: manualConfirmation,
            });
            setNodeDetailsOpen(false);
            setSelectedNodeId();
          } catch (error) {
            console.error("Error al guardar el campo calculado:", error);
          }
        }}
      >
        <ThreeColumnLayout
          className="overflow-auto"
          leftHeader={{
            icon: "arrow-right-to-bracket",
            title: "Inputs",
            color: "neutral",
            hideSearch: true,
          }}
          centerHeader={{
            title: title,
            icon: "function",
            color: "success",
            editable: true,
            onTitleChange: (newTitle: string) => {
              setTitle(newTitle);
              if (newTitle.trim() === "") {
                setShowNodeTitleValidation(true);
              }
            },
            onTitleBlur: () => setShowNodeTitleValidation(true),
            validationError: getNameValidationError(title),
            showValidation: showNodeTitleValidation,
            manualConfirmation,
            onManualConfirmationChange: setManualConfirmation,
          }}
          rightHeader={{
            icon: "arrow-right-from-bracket",
            title: "Outputs",
            color: "neutral",
          }}
          leftContent={
            <>
              <div className="bg-neutral-foreground overflow-auto divide-y divide-(--border-neutral-quiet)">
                <Variables sourceNodeId={selectedNodeId!} />
              </div>
            </>
          }
          leftContentWidth="min-w-[445px]"
          centerContent={
            <>
              {isLoadingVariables ? (
                <div>
                  <p className="min-h-[48px] text-body-loud font-semibold flex justify-center items-center py-0.5 px-3 gap-4 ">
                    <Icon
                      type="far"
                      name="spinner-third"
                      className="animate-spin text-xl text-body"
                    />
                  </p>
                </div>
              ) : (
                <CalculatedFieldOperation />
              )}
            </>
          }
          rightContent={
            <>
              <div className="bg-neutral-foreground overflow-auto divide-y divide-(--border-neutral-quiet)">
                <InputOutput
                  nodeType={NodeTypes.CALCULATED}
                  showSteps={false}
                  variableName={title || undefined}
                  nodeLabel={title}
                  variableType={selectedOperation?.output || ""}
                />
              </div>
            </>
          }
        />
      </FullModal>
    </>
  );
};

export const CalculatedFieldDetails = () => {
  const { variables, isLoading } = useGetVariables();
  return (
    <CalculatedFieldProvider
      variables={variables}
      isLoadingVariables={isLoading}
    >
      <CalculatedFieldDetailsForm />
    </CalculatedFieldProvider>
  );
};
